import { useState, useRef, useEffect, Dispatch, SetStateAction } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  ArrowUpRight,
  ArrowUpRightIcon,
  ArrowDown,
} from "lucide-react";
import { CreatorAction } from "@/types/types";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import EmptyState from "@/components/ui/empty-state";
import { CopyButton } from "@/components/ui/copy-button";
import { ScrollArea } from "@/components/ui/scroll-area";
import WaitingApprovalAction from "./action-details/WaitingApprovalAction";
import HandoffAction from "./action-details/HandoffAction";
import NextMessageAction from "./action-details/NextMessageAction";
import PhaseReviewAction from "./action-details/PhaseReviewAction";
import PayoutConfirmAction from "./action-details/PayoutConfirmAction";
import EveAttentionAction from "./action-details/EveAttentionAction";
import PerformancePaymentAction from "./action-details/PerformancePaymentAction";
import ContractChangeAction from "./action-details/ContractChangeAction";
import OfferReviewAction from "./action-details/OfferReviewAction";

interface ActionDetailsPanelProps {
  selectedAction: CreatorAction | null;
  emailThread?: any[];
  threadLoading?: boolean;
  setActionLoading: Dispatch<SetStateAction<boolean>>;
}

export default function ActionDetailsPanel({
  selectedAction,
  setActionLoading,
  threadLoading = false,
  emailThread = [],
}: ActionDetailsPanelProps) {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  useEffect(() => {
    const target = bottomRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsAtBottom(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [selectedAction?._id, emailThread.length, threadLoading]);

  const showHandleTask =
    !isAtBottom &&
    emailThread.length > 0 &&
    (selectedAction?.actionType === "handoff" ||
      selectedAction?.actionType === "next-message");

  if (!selectedAction) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <EmptyState icon={<CheckCircle />} text="Choose a pending action!" />
      </div>
    );
  }

  const profileLink =
    selectedAction.campaign_platform === "tiktok"
      ? `https://www.tiktok.com/@${selectedAction.platform_id}`
      : selectedAction.campaign_platform === "youtube"
        ? `https://www.youtube.com/channel/${selectedAction.platform_id}`
        : "https://www.instagram.com/" + selectedAction.username;

  return (
    <div className="relative h-full">
      <ScrollArea scrollFade className="h-full">
        <div className="h-full flex flex-col gap-4 py-6 overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]">
          <div className="px-6 flex items-center justify-between">
            <Button
              variant="ghost"
              className="group [&_svg]:size-3 relative flex flex-row items-center gap-2 h-12 pl-1 pr-2 hover:bg-muted-foreground/15 rounded-lg transition-all"
              onClick={() => window.open(profileLink)}
            >
              <Avatar className="size-10 border">
                <AvatarImage
                  src={selectedAction.avatar}
                  alt={selectedAction.username}
                />
                <AvatarFallback className="font-semibold">
                  {selectedAction.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="font-medium text-sm">
                  @{selectedAction.username}
                </span>
                <span className="text-muted-foreground font-normal">
                  {selectedAction.social?.email}
                </span>
              </div>
              <ArrowUpRightIcon className="shrink-0 absolute top-2 right-2 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.75 group-hover:-translate-y-0.75 transition-all text-foreground" />
            </Button>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <CopyButton
                buttonText=""
                textToCopy={selectedAction._id || ""}
                tooltipText="Copy campaign creator ID"
                className="size-7 [&_svg]:size-3.5 rounded-md"
                variant="secondary"
              />
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    className="w-auto justify-between h-7 rounded-md pr-1.5"
                    onClick={() =>
                      navigate(
                        `/dashboard/campaigns/${selectedAction.campaign_id}/creators?query=${selectedAction.username}&creatorId=${selectedAction._id}`
                      )
                    }
                    size="sm"
                  >
                    {`${selectedAction.campaign_name.slice(0, 24)}...`}
                    <ArrowUpRight className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row items-center gap-1">
                  <p>Go to: {selectedAction.campaign_name} </p>
                  <ArrowUpRight className="size-3" />
                </TooltipContent>
              </Tooltip>
              <BadgeStatus status={selectedAction.actionType} />
            </div>
          </div>

          <div className="px-6 flex-1 flex gap-4 flex-col">
            {selectedAction.actionType === "waiting-approval" && (
              <WaitingApprovalAction action={selectedAction} />
            )}

            {selectedAction.actionType === "handoff" && (
              <HandoffAction
                action={selectedAction}
                emailThread={emailThread}
                threadLoading={threadLoading}
                setActionLoading={setActionLoading}
              />
            )}

            {selectedAction.actionType === "next-message" && (
              <NextMessageAction
                action={selectedAction}
                emailThread={emailThread}
                threadLoading={threadLoading}
              />
            )}

            {selectedAction.actionType === "phase-review" && (
              <PhaseReviewAction action={selectedAction} />
            )}

            {selectedAction.actionType === "payout-confirm" && (
              <PayoutConfirmAction action={selectedAction} />
            )}

            {selectedAction.actionType === "eve-attention" && (
              <EveAttentionAction action={selectedAction} />
            )}

            {selectedAction.actionType === "performance-payment" && (
              <PerformancePaymentAction action={selectedAction} />
            )}

            {selectedAction.actionType === "contract-change" && (
              <ContractChangeAction
                action={selectedAction}
                emailThread={emailThread}
                threadLoading={threadLoading}
              />
            )}

            {selectedAction.actionType === "offer-review" && (
              <OfferReviewAction
                action={selectedAction}
                emailThread={emailThread}
                threadLoading={threadLoading}
              />
            )}

            <div ref={bottomRef} />
          </div>
        </div>
      </ScrollArea>
      <div
        className={`absolute bottom-4 left-1/2 z-10 pointer-events-none transition-all duration-200 ease-out ${showHandleTask
          ? "opacity-100 -translate-x-1/2 translate-y-0"
          : "opacity-0 -translate-x-1/2 translate-y-2"
          }`}
        aria-hidden={!showHandleTask}
      >
        <Button
          variant="neutral"
          size="sm"
          onClick={scrollToBottom}
          tabIndex={showHandleTask ? 0 : -1}
          className={`shadow-lg ${showHandleTask ? "pointer-events-auto" : "pointer-events-none"
            }`}
        >
          Handle Task
          <ArrowDown className="size-4" />
        </Button>
      </div>
    </div>
  );
}
